import { registerIndicator } from "klinecharts";

type KLinePoint = {
  high: number;
  low: number;
  close: number;
};

let indicatorsRegistered = false;

function calcTrueRange(
  current: KLinePoint,
  previous: KLinePoint | undefined,
) {
  if (!previous) {
    return current.high - current.low;
  }

  return Math.max(
    current.high - current.low,
    Math.abs(current.high - previous.close),
    Math.abs(current.low - previous.close),
  );
}

export function registerCustomIndicators() {
  if (indicatorsRegistered) {
    return;
  }

  indicatorsRegistered = true;

  registerIndicator({
    name: "LYTICK_ATR",
    shortName: "ATR",
    precision: 4,
    shouldOhlc: false,
    shouldFormatBigNumber: false,
    visible: true,
    series: "normal",
    calcParams: [14],
    figures: [{ key: "atr", title: "ATR: ", type: "line" }],
    calc: (dataList, indicator) => {
      const period = Math.max(
        1,
        Math.floor(Number(indicator.calcParams?.[0]) || 14),
      );
      let previousAtr = 0;
      const trueRanges: number[] = [];

      return dataList.map((current, index) => {
        const previous = dataList[index - 1];
        const trueRange = calcTrueRange(current, previous);
        trueRanges.push(trueRange);

        if (index < period - 1) {
          return {};
        }

        if (index === period - 1) {
          previousAtr =
            trueRanges.slice(index - period + 1, index + 1).reduce(
              (sum, value) => sum + value,
              0,
            ) / period;
          return { atr: previousAtr };
        }

        previousAtr = (previousAtr * (period - 1) + trueRange) / period;
        return { atr: previousAtr };
      });
    },
  });

  registerIndicator({
    name: "LYTICK_STOCH",
    shortName: "STOCH",
    precision: 2,
    shouldOhlc: false,
    shouldFormatBigNumber: false,
    visible: true,
    series: "normal",
    calcParams: [14, 3],
    figures: [
      { key: "k", title: "%K: ", type: "line" },
      { key: "d", title: "%D: ", type: "line" },
    ],
    calc: (dataList, indicator) => {
      const kPeriod = Math.max(
        1,
        Math.floor(Number(indicator.calcParams?.[0]) || 14),
      );
      const dPeriod = Math.max(
        1,
        Math.floor(Number(indicator.calcParams?.[1]) || 3),
      );
      const kValues: number[] = [];

      return dataList.map((current, index) => {
        if (index < kPeriod - 1) {
          return {};
        }

        const window = dataList.slice(index - kPeriod + 1, index + 1);
        const lowestLow = Math.min(...window.map((item) => item.low));
        const highestHigh = Math.max(...window.map((item) => item.high));
        const denominator = highestHigh - lowestLow;
        const k =
          denominator === 0
            ? 50
            : ((current.close - lowestLow) / denominator) * 100;

        kValues.push(k);

        if (kValues.length < dPeriod) {
          return { k };
        }

        const d =
          kValues.slice(kValues.length - dPeriod).reduce(
            (sum, value) => sum + value,
            0,
          ) / dPeriod;

        return { k, d };
      });
    },
  });

  registerIndicator({
    name: "LYTICK_SUPERTREND",
    shortName: "ST",
    precision: 2,
    shouldOhlc: false,
    shouldFormatBigNumber: false,
    visible: true,
    series: "price",
    calcParams: [10, 3],
    figures: [
      { key: "supertrend", title: "ST: ", type: "line" },
    ],
    regenerateFigures: (params) => [
      { key: "supertrend", title: `ST(${params[0]},${params[1]}): `, type: "line" },
    ],
    calc: (dataList, indicator) => {
      const period = Math.max(1, Math.floor(Number(indicator.calcParams?.[0]) || 10));
      const multiplier = Math.max(0.1, Number(indicator.calcParams?.[1]) || 3);

      let prevUpperBand = 0;
      let prevLowerBand = 0;
      let prevSuperTrend = 0;
      const atrValues: number[] = [];

      return dataList.map((kline, i) => {
        if (i === 0) {
          atrValues.push(0);
          return { supertrend: null };
        }

        const prev = dataList[i - 1];
        const tr = calcTrueRange(kline, prev);
        atrValues.push(tr);

        if (i < period) {
          return { supertrend: null };
        }

        const atr = atrValues.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0) / period;
        const hl2 = (kline.high + kline.low) / 2;
        let upperBand = hl2 + multiplier * atr;
        let lowerBand = hl2 - multiplier * atr;

        // Ratchet bands — they never move against the trend
        if (i > period) {
          upperBand = (upperBand < prevUpperBand || prev.close > prevUpperBand) ? upperBand : prevUpperBand;
          lowerBand = (lowerBand > prevLowerBand || prev.close < prevLowerBand) ? lowerBand : prevLowerBand;
        }

        let direction: number;
        if (prevSuperTrend === prevUpperBand) {
          direction = kline.close > upperBand ? 1 : -1;
        } else {
          direction = kline.close < lowerBand ? -1 : 1;
        }
        const supertrend = direction === 1 ? lowerBand : upperBand;

        prevUpperBand = upperBand;
        prevLowerBand = lowerBand;
        prevSuperTrend = supertrend;

        return { supertrend };
      });
    },
  });

  registerIndicator({
    name: "LYTICK_ICHIMOKU",
    shortName: "ICH",
    precision: 2,
    shouldOhlc: false,
    shouldFormatBigNumber: false,
    visible: true,
    series: "price",
    calcParams: [9, 26, 52, 26],
    figures: [
      { key: "tenkan", title: "Tenkan: ", type: "line" },
      { key: "kijun", title: "Kijun: ", type: "line" },
      { key: "senkouA", title: "Senkou A: ", type: "line" },
      { key: "senkouB", title: "Senkou B: ", type: "line" },
      { key: "chikou", title: "Chikou: ", type: "line" },
    ],
    calc: (dataList, indicator) => {
      const convPeriod = Math.max(1, Math.floor(Number(indicator.calcParams?.[0]) || 9));
      const basePeriod = Math.max(1, Math.floor(Number(indicator.calcParams?.[1]) || 26));
      const spanBPeriod = Math.max(1, Math.floor(Number(indicator.calcParams?.[2]) || 52));
      const displacement = Math.max(1, Math.floor(Number(indicator.calcParams?.[3]) || 26));

      const periodHighLow = (end: number, period: number) => {
        const slice = dataList.slice(Math.max(0, end - period + 1), end + 1);
        const high = Math.max(...slice.map((k: any) => k.high));
        const low = Math.min(...slice.map((k: any) => k.low));
        return (high + low) / 2;
      };

      // Pre-allocate result array
      const result: Record<string, any>[] = new Array(dataList.length);

      for (let i = 0; i < dataList.length; i++) {
        const kline = dataList[i];
        const entry: Record<string, any> = {};

        const tenkan = i >= convPeriod - 1 ? periodHighLow(i, convPeriod) : null;
        const kijun = i >= basePeriod - 1 ? periodHighLow(i, basePeriod) : null;

        entry.tenkan = tenkan;
        entry.kijun = kijun;

        // Senkou A — displaced forward
        const senkouAIdx = i + displacement;
        if (tenkan !== null && kijun !== null && senkouAIdx < dataList.length) {
          if (!result[senkouAIdx]) result[senkouAIdx] = {};
          result[senkouAIdx].senkouA = (tenkan + kijun) / 2;
        }

        // Senkou B — displaced forward
        if (i >= spanBPeriod - 1) {
          const senkouBIdx = i + displacement;
          if (senkouBIdx < dataList.length) {
            if (!result[senkouBIdx]) result[senkouBIdx] = {};
            result[senkouBIdx].senkouB = periodHighLow(i, spanBPeriod);
          }
        }

        // Chikou — displaced backward
        const chikouIdx = i - displacement;
        if (chikouIdx >= 0) {
          if (!result[chikouIdx]) result[chikouIdx] = {};
          result[chikouIdx].chikou = kline.close;
        }

        // Merge with any values already set by forward/backward displacement
        result[i] = { ...entry, ...(result[i] || {}) };
      }

      return result;
    },
  });

  // ── Pivot Points (Classic Floor Trader Pivots) ────────────────────────────
  // Uses previous day's HLC to calculate current day's support/resistance levels
  // For 24/7 synthetic indices, uses previous period's HLC

  registerIndicator({
    name: "LYTICK_PIVOTS",
    shortName: "PIVOTS",
    precision: 2,
    shouldOhlc: false,
    shouldFormatBigNumber: false,
    visible: true,
    series: "price",
    calcParams: [],
    figures: [
      { key: "r3", title: "R3: ", type: "line" },
      { key: "r2", title: "R2: ", type: "line" },
      { key: "r1", title: "R1: ", type: "line" },
      { key: "pivot", title: "P: ", type: "line" },
      { key: "s1", title: "S1: ", type: "line" },
      { key: "s2", title: "S2: ", type: "line" },
      { key: "s3", title: "S3: ", type: "line" },
    ],
    calc: (dataList) => {
      // Use previous candle for pivots (for intraday, use previous day)
      const result: Record<string, any>[] = [];

      for (let i = 0; i < dataList.length; i++) {
        const current = dataList[i];
        const previous = i > 0 ? dataList[i - 1] : null;

        if (!previous) {
          result.push({});
          continue;
        }

        const high = previous.high;
        const low = previous.low;
        const close = previous.close;

        const pivot = (high + low + close) / 3;
        const r1 = (2 * pivot) - low;
        const s1 = (2 * pivot) - high;
        const r2 = pivot + (high - low);
        const s2 = pivot - (high - low);
        const r3 = high + 2 * (pivot - low);
        const s3 = low - 2 * (high - pivot);

        result.push({
          pivot,
          r1,
          r2,
          r3,
          s1,
          s2,
          s3,
        });
      }

      return result;
    },
  });
}
